import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { SUBMIT_REFERENCE } from 'graphql/resolvers/admin/candidates/mutations';
import FormUploader from 'components/Uploader';
import { REFERENCE_SUBMISSION } from 'graphql/resolvers/client/queries';
import { referenceSubmitValidationSchema } from 'helpers/validators';
import { toastSuccess, toastError } from 'components/common/Toast';
import Loader from 'components/common/Loader';
import { svgs } from 'assets';

const PersonalReference = ({
  match: { params },
  employeeName = 'The Employee',
  prevTab
}) => {
  const requestId = params.id;
  const [showLoader, setLoaderState] = useState(false);
  const [createReferenceRequest] = useMutation(SUBMIT_REFERENCE);

  const { data: submissionData } = useQuery(REFERENCE_SUBMISSION, {
    variables: {
      referenceId: requestId
    },
    skip: !requestId
  });

  const isReadOnly = !!submissionData?.referenceSubmission;
  const parsedExtraData = submissionData?.referenceSubmission?.formData
    ? JSON.parse(submissionData?.referenceSubmission?.formData)
    : {};

  // Styled to reflect a structured form
  const styles = {
    wrapper: { background: '#f8fafc', minHeight: '100vh', padding: '40px 10px' },
    card: {
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      maxWidth: '1000px',
      margin: '0 auto',
      background: '#fff'
    },
    header: {
      padding: '50px 30px',
      borderBottom: '2px solid #e2e8f0',
      textAlign: 'center'
    },
    mainTitle: {
      // fontSize: '3.5rem',
      fontWeight: '900',
      color: '#0f172a',
      textTransform: 'uppercase'
    },
    label: { fontWeight: '600', color: '#1e293b' },
    subText: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#475569',
      marginTop: '10px'
    },
    formBody: { padding: '40px' },
    questionLabel: { fontWeight: '600', color: '#1e293b', marginBottom: '10px' },
    input: {
      padding: '12px',
      borderRadius: '6px',
      border: '1px solid #cbd5e1',
      fontSize: '1.2rem'
    },
    radioOption: {
      border: '1px solid #cbd5e1',
      borderRadius: '6px',
      cursor: isReadOnly ? 'default' : 'pointer',
      transition: 'all 0.2s'
    },
    requiredAsterisk: { color: 'red', marginLeft: '3px' }
  };

  // Define options exactly as per the image
  const options = {
    relationship: ['Line Manager', 'Colleague', 'Subordinate', 'Friend'],
    duration: ['Under 1 year', '1-5 years', '5-10 years', 'Over 10 years'],
    honest: ['Yes', 'No', 'Not Certain'],
    recommend: ['Yes', 'No'],
    motivation: ['Great Extent', 'Low Extent', 'Not motivated & Driven']
  };

  const handleOnSubmit = async (values) => {
    setLoaderState(true);
    try {
      const response = await createReferenceRequest({
        variables: {
          input: {
            requestId: requestId,
            fullname: values.refereeName,
            email: values.email,
            phone: values.phone,
            address: values.address,
            // Pack all specific assessment questions into formData JSON string
            formData: JSON.stringify({
              question_1_relationship: values.relationship,
              question_1_comments: values.relationshipComments,
              question_2_duration: values.durationKnown,
              question_2_comments: values.durationComments,
              question_3_honesty: values.isHonest,
              question_4_recommend: values.recommendation,
              question_4_reason: values.recommendationReason,
              question_5_motivation: values.motivationLevel,
              generalComments: values.generalComments,
              occupation: values.occupation
            })
          }
        }
      });

      if (response.data?.submitReferenceForm?.ok) {
        toastSuccess(svgs.SUCCESS_ICON, 'Reference Submitted Successfully');
      } else {
        const errors = response.data?.submitReferenceForm?.errors?.messages.join(', ');
        toastError(svgs.DELETE_ICON, errors || 'Submission failed.');
      }
    } catch (e) {
      const errors = e?.data?.submitReferenceForm?.errors?.messages.join(', ');
      toastError(svgs.DELETE_ICON, errors || 'An error occurred during submission.');
    } finally {
      setLoaderState(false);
    }
  };

  return (
    <div style={styles.wrapper}>
      <Formik
        enableReinitialize
        initialValues={{
          // Physical Form Mapping
          relationship: parsedExtraData.question_1_relationship || '',
          relationshipComments: parsedExtraData.question_1_comments || '',
          durationKnown: parsedExtraData.question_2_duration || '',
          durationComments: parsedExtraData.question_2_comments || '',
          isHonest: parsedExtraData.question_3_honesty || '',
          recommendation: parsedExtraData.question_4_recommend || '',
          recommendationReason: parsedExtraData.question_4_reason || '',
          motivationLevel: parsedExtraData.question_5_motivation || '',
          generalComments: parsedExtraData.generalComments || '',
          refereeName: submissionData?.referenceSubmission?.fullname || '',
          address: submissionData?.referenceSubmission?.address || '',
          phone: submissionData?.referenceSubmission?.phone || '',
          email: submissionData?.referenceSubmission?.email || '',
          occupation: parsedExtraData.occupation || '',
          agreement: false // Checkbox control
        }}
        validationSchema={referenceSubmitValidationSchema}
        onSubmit={handleOnSubmit}
      >
        {({
          values,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
          isValid,
          dirty,
          errors
        }) => (
          <div className="container px-0">
            {console.log('errors', errors)}
            <div className="card" style={styles.card}>
              <div style={styles.header}>
                <h1 style={styles.mainTitle}>REFERENCE CHECK</h1>
                <p style={styles.subText}>
                  Any information given will be treated in the strictest of confidence.
                </p>
              </div>

              <Form style={styles.formBody}>
                {/* REFEREE CERTIFICATION SECTION */}
                <div className="pt-4 border-top">
                  {/* <h2 style={styles.certificationHeader}>Referee Information</h2> */}

                  <div className="row mb-5 pb-3 border-bottom">
                    <div className="col-md-6 py-3">
                      <label style={styles.label}>
                        Full Name: <span style={styles.requiredAsterisk}>*</span>
                      </label>
                      {/* <label className="fw-bold text-muted">Full Name</label> */}
                      <Field
                        name="refereeName"
                        as="textarea"
                        rows="1"
                        className="form-control"
                        style={{
                          ...styles.input,
                          resize: 'none',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden'
                        }}
                        disabled={isReadOnly}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                          }
                        }}
                      />
                      <ErrorMessage
                        name="refereeName"
                        component="div"
                        className="text-danger small mt-1"
                      />
                    </div>

                    <div className="col-md-6 py-3">
                      {/* <label className="fw-bold text-muted">Occupation</label> */}
                      <label style={styles.label}>
                        Occupation: <span style={styles.requiredAsterisk}>*</span>
                      </label>
                      <Field
                        name="occupation"
                        as="textarea"
                        rows="1"
                        className="form-control"
                        style={{
                          ...styles.input,
                          resize: 'none',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden'
                        }}
                        disabled={isReadOnly}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                          }
                        }}
                      />
                      <ErrorMessage
                        name="occupation"
                        component="div"
                        className="text-danger small mt-1"
                      />
                    </div>

                    <div className="col-12 py-2">
                      {/* <label className="fw-bold small text-muted">Physical Address</label> */}
                      <label style={styles.label}>
                        Address: <span style={styles.requiredAsterisk}>*</span>
                      </label>
                      <Field
                        name="address"
                        as="textarea"
                        rows="2"
                        className="form-control"
                        style={styles.input}
                        disabled={isReadOnly}
                      />
                      <ErrorMessage
                        name="address"
                        component="div"
                        className="text-danger small mt-1"
                      />
                    </div>

                    <div className="col-md-6 py-3">
                      {/* <label className="fw-bold small text-muted">phone Number</label> */}
                      <label style={styles.label}>
                        Phone Number: <span style={styles.requiredAsterisk}>*</span>
                      </label>
                      <Field
                        name="phone"
                        as="textarea"
                        rows="1"
                        className="form-control"
                        style={{
                          ...styles.input,
                          resize: 'none',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden'
                        }}
                        disabled={isReadOnly}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                          }
                        }}
                      />
                      <ErrorMessage
                        name="phone"
                        component="div"
                        className="text-danger small mt-1"
                      />
                    </div>

                    <div className="col-md-6 py-3">
                      {/* <label className="fw-bold small text-muted">Email Address</label> */}
                      <label style={styles.label}>
                        Email Address: <span style={styles.requiredAsterisk}>*</span>
                      </label>
                      <Field
                        name="email"
                        as="textarea"
                        rows="1"
                        className="form-control"
                        style={{
                          ...styles.input,
                          resize: 'none',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden'
                        }}
                        disabled={isReadOnly}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                          }
                        }}
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="text-danger small mt-1"
                      />
                    </div>
                  </div>
                </div>
                {/* 1. Relationship */}
                <div className="row mb-5 border-bottom pb-4">
                  <div className="col-md-5">
                    <label style={styles.questionLabel}>
                      1. Relationship with employee?{' '}
                      <span style={styles.requiredAsterisk}>*</span>
                    </label>
                  </div>
                  <div className="col-md-7">
                    <div className="row g-2 mb-2">
                      {options.relationship.map((opt) => (
                        <div key={opt} className="col-6">
                          <label
                            style={styles.radioOption}
                            className={`w-100 p-3 text-center ${
                              values.relationship === opt
                                ? 'bg-primary text-white'
                                : 'bg-white'
                            }`}
                          >
                            <Field
                              type="radio"
                              name="relationship"
                              value={opt}
                              disabled={isReadOnly}
                              className="d-none"
                            />
                            {opt}
                          </label>
                        </div>
                      ))}
                    </div>
                    <ErrorMessage
                      name="relationship"
                      component="div"
                      className="text-danger small mb-3"
                    />
                    <Field
                      name="relationshipComments"
                      as="textarea"
                      disabled={isReadOnly}
                      placeholder="Comments"
                      className="form-control"
                      style={styles.input}
                    />
                  </div>
                </div>

                {/* 2. Duration */}
                <div className="row mb-5 pb-3 border-bottom">
                  <div className="col-md-5">
                    <label style={styles.questionLabel}>
                      <span className="fw-bold me-2">2.</span>How long have you known the
                      employee?
                    </label>
                  </div>
                  <div className="col-md-7">
                    <div className="row g-2 mb-3">
                      {options.duration.map((opt) => (
                        <div key={opt} className="col-6">
                          <label
                            style={styles.radioOption}
                            className={`w-100 p-3 ${
                              values.durationKnown === opt
                                ? 'bg-primary text-white'
                                : 'bg-white'
                            }`}
                          >
                            <Field
                              type="radio"
                              name="durationKnown"
                              value={opt}
                              disabled={isReadOnly}
                              className="d-none"
                            />
                            {opt}
                          </label>
                        </div>
                      ))}
                    </div>
                    <ErrorMessage
                      name="durationKnown"
                      component="div"
                      className="text-danger small mb-3"
                    />
                    <label className="text-muted small mb-1">Comments:</label>
                    <Field
                      name="durationComments"
                      as="textarea"
                      rows="2"
                      className="form-control"
                      style={styles.input}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>

                {/* 3. Honesty */}
                <div className="row mb-5 pb-3 border-bottom">
                  <div className="col-md-5">
                    <label style={styles.questionLabel}>
                      <span className="fw-bold me-2">3.</span>Do you consider him/her to
                      be honest?
                    </label>
                  </div>
                  <div className="col-md-7">
                    <div className="d-flex gap-2">
                      {options.honest.map((opt) => (
                        <label
                          key={opt}
                          style={styles.radioOption}
                          className={`flex-grow-1 p-3 text-center ${
                            values.isHonest === opt ? 'bg-primary text-white' : 'bg-white'
                          }`}
                        >
                          <Field
                            type="radio"
                            name="isHonest"
                            value={opt}
                            disabled={isReadOnly}
                            className="d-none"
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                    <ErrorMessage
                      name="isHonest"
                      component="div"
                      className="text-danger small mt-2"
                    />
                  </div>
                </div>

                {/* 4. Recommendation */}
                <div className="row mb-5 pb-3 border-bottom">
                  <div className="col-md-5">
                    <label style={styles.questionLabel}>
                      <span className="fw-bold me-2">4.</span>Would you recommend him/her
                      for employment with our client and why?
                    </label>
                  </div>
                  <div className="col-md-7">
                    <div className="d-flex gap-2 mb-3">
                      {options.recommend.map((opt) => (
                        <label
                          key={opt}
                          style={styles.radioOption}
                          className={`flex-grow-1 p-3 text-center ${
                            values.recommendation === opt
                              ? 'bg-primary text-white'
                              : 'bg-white'
                          }`}
                        >
                          <Field
                            type="radio"
                            name="recommendation"
                            value={opt}
                            disabled={isReadOnly}
                            className="d-none"
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                    <ErrorMessage
                      name="recommendation"
                      component="div"
                      className="text-danger small mb-3"
                    />
                    <label className="text-muted small mb-1">Reason:</label>
                    <Field
                      name="recommendationReason"
                      as="textarea"
                      rows="2"
                      className="form-control"
                      style={styles.input}
                      disabled={isReadOnly}
                    />
                    <ErrorMessage
                      name="recommendationReason"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </div>
                </div>

                {/* 5. Motivation */}
                <div className="row mb-5 pb-3 border-bottom">
                  <div className="col-md-5">
                    <label style={styles.questionLabel}>
                      <span className="fw-bold me-2">5.</span>To what extent would you say
                      he is self-motivated and driven to achieve result?
                    </label>
                  </div>
                  <div className="col-md-7">
                    <div className="row g-2">
                      {options.motivation.map((opt) => (
                        <div key={opt} className="col-12">
                          <label
                            style={styles.radioOption}
                            className={`w-100 p-3 ${
                              values.motivationLevel === opt
                                ? 'bg-primary text-white'
                                : 'bg-white'
                            }`}
                          >
                            <Field
                              type="radio"
                              name="motivationLevel"
                              value={opt}
                              disabled={isReadOnly}
                              className="d-none"
                            />
                            {opt}
                          </label>
                        </div>
                      ))}
                    </div>
                    <ErrorMessage
                      name="motivationLevel"
                      component="div"
                      className="text-danger small mt-2"
                    />
                  </div>
                </div>

                {/* General Comments */}
                <div className="mb-5">
                  <label style={styles.sectionLabel}>Comments:</label>
                  <Field
                    name="generalComments"
                    as="textarea"
                    rows="4"
                    className="form-control mt-2"
                    style={styles.input}
                    disabled={isReadOnly}
                    placeholder="Provide any additional remarks here..."
                  />
                </div>

                {/* Agreement Check & Submit */}
                {!isReadOnly && (
                  <div className="mt-5 pt-4 border-top">
                    <div className="p-4 rounded border bg-light mb-4">
                      <div className="d-flex align-items-start">
                        <Field
                          type="checkbox"
                          name="agreement"
                          className="form-check-input me-3 mt-1"
                          id="agreementCheck"
                          style={{
                            width: '20px',
                            height: '20px',
                            flexShrink: 0,
                            cursor: 'pointer'
                          }}
                        />
                        <label
                          className="form-check-label fw-bold text-dark"
                          htmlFor="agreementCheck"
                          style={{ cursor: 'pointer', lineHeight: '1.5' }}
                        >
                          I hereby certify that I am voluntarily standing as a referee for{' '}
                          {employeeName} and that the information provided above is true
                          and correct to the best of my knowledge.
                          <span style={styles.requiredAsterisk}>*</span>
                        </label>
                      </div>
                      <ErrorMessage
                        name="agreement"
                        component="div"
                        className="text-danger small mt-2 ms-5"
                      />
                    </div>

                    <div className="d-flex justify-content-end gap-3">
                      <button
                        type="submit"
                        className="btn btn-primary px-5 py-3 fw-bold"
                        // Disabled until form is valid, dirty, and agreement is checked
                        // disabled={
                        //   showLoader ||
                        //   isSubmitting ||
                        //   !values.agreement ||
                        //   !isValid ||
                        //   !dirty
                        // }
                      >
                        {showLoader ? <Loader /> : 'SUBMIT REFERENCE'}
                      </button>
                    </div>
                  </div>
                )}
              </Form>
            </div>
          </div>
        )}
      </Formik>
    </div>
  );
};

export default PersonalReference;
